// selectors.ts
import { createSelector, createFeatureSelector } from '@ngrx/store';
import { TemplateState } from './template.reducers';
import { cloneDeep } from 'lodash';
import { CustomSlug } from '../../models/CustomSlug.model';
import { CustomComponent } from '../../models/CustomComponent.model';
import { ComponentRegistryService } from '../../services/component-registry/component-registry.service';
import { CustomTemplate } from '../../models/CustomTemplate.model';
import { environment } from '../../environment/environment';

export const selectTemplateState = (state: any) => state.template;

export const selectSlugData = (slug: string) => createSelector(
  selectTemplateState,
  (state: TemplateState) => {
    return state?.slugs?.[slug];
  }
);

export const selectCustomSlug = (slug: string) => createSelector(
  selectSlugData(slug),
  (slugData) => slugData?.customSlug
);

export function createSelectStaticTemplate(componentRegistry: ComponentRegistryService) {
  const memoizedSelectors = new Map<string, any>();
  
  return (params: { uuid: string; slug: string; currentMode: 1 | 2 | 3 }) => {
    const key = `${params.uuid}_${params.slug}_${params.currentMode}`;
    
    if (!memoizedSelectors.has(key)) {
      if (environment.debug) {
      }
      const selector = createSelector(
        selectTemplateState,
        (state: TemplateState) => {
          if (!state) return null;

          const slugData = state.slugs[params.slug];
          if (!slugData?.staticTemplate || !slugData?.customSlug) return null;

          // First check if we have a template for the current mode
          const templateUuid = slugData.customSlug.getStaticTemplateFromMode(params.currentMode);
          
          // If no template found or UUID doesn't match, return null
          if (!templateUuid || templateUuid !== params.uuid) {
            return null;
          }

          if (environment.debug) {
            }

          const template = cloneDeep(slugData.staticTemplate);
          return processTemplate(template, componentRegistry);
        }
      );
      memoizedSelectors.set(key, selector);
    }
    
    return memoizedSelectors.get(key);
  };
}

export function createSelectDynamicTemplate(componentRegistry: ComponentRegistryService) {
  const memoizedSelectors = new Map<string, any>();
  
  return (params: { uuid: string; slug: string; currentMode: number }) => {
    const key = `${params.uuid}_${params.slug}_${params.currentMode}`;
    
    if (!memoizedSelectors.has(key)) {
      const selector = createSelector(
        selectTemplateState,
        (state: TemplateState) => {
          if (!state) return null;

          const slugData = state.slugs[params.slug];
          if (!slugData?.dynamicTemplate) return null;

          const template = cloneDeep(slugData.dynamicTemplate);
          return processTemplate(template, componentRegistry);
        }
      );
      memoizedSelectors.set(key, selector);
    }
    
    return memoizedSelectors.get(key);
  };
}

function processTemplate(template: CustomTemplate, componentRegistry: ComponentRegistryService): CustomTemplate {
  template.customComponents?.forEach(component => {
    const comp = componentRegistry.getComponentByName(component.name);
    if (comp) {
      component.component = comp.component;
    } else {
      component.component = null;
    }
  });
  return template;
}
